import { useEffect, useState } from 'react'

type DocTier = 'dev' | 'staging' | 'prod'

interface CatalogItem {
  id: string
  title?: string
  tier: DocTier
  confidence: number
  owner?: string | null
  last_reviewed?: string | null
  version?: string | null
  origin: 'repo' | 'notion'
  path?: string
}

export function DocsDashboard() {
  const [items, setItems] = useState<CatalogItem[]>([])
  const [counts, setCounts] = useState<{ total: number, byTier: Record<DocTier, number> }>({ total: 0, byTier: { dev: 0, staging: 0, prod: 0 } })
  const [, setStale] = useState<CatalogItem[]>([])
  const [filter, setFilter] = useState<DocTier | 'all'>('all')

  useEffect(() => {
    fetch('/docs-catalog.json')
      .then(r => r.json())
      .then((payload) => {
        const items = payload.items || []
        const counts = items.reduce(
          (acc: any, it: CatalogItem) => {
            acc.total++
            acc.byTier[it.tier] = (acc.byTier[it.tier] || 0) + 1
            return acc
          },
          { total: 0, byTier: { dev: 0, staging: 0, prod: 0 } }
        )
        const stale = items.filter((it: CatalogItem) => {
          if (!it.last_reviewed) return true
          const d = new Date(it.last_reviewed)
          if (isNaN(d.getTime())) return true
          const ageDays = (Date.now() - d.getTime()) / 86400000
          return ageDays > 90
        })
        setItems(items)
        setCounts(counts)
        setStale(stale)
      })
      .catch(() => {})
  }, [])

  const filtered = items.filter(i => filter === 'all' ? true : i.tier === filter)

  const copyPromote = (path?: string, to: DocTier = 'staging') => {
    if (!path) return
    const cmd = `npm run docs:promote -- ${path} --to ${to}`
    navigator.clipboard.writeText(cmd).catch(() => {})
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Documentation</h1>
        <p className="text-charcoal-600">Track doc tiers, staleness, and promote quickly.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[var(--bg-raised)] border p-4">
          <div className="text-sm text-charcoal-600">Total</div>
          <div className="text-2xl font-semibold">{counts.total}</div>
        </div>
        <div className="bg-[var(--bg-raised)] border p-4">
          <div className="text-sm text-charcoal-600">Prod</div>
          <div className="text-2xl font-semibold">{counts.byTier.prod}</div>
        </div>
        <div className="bg-[var(--bg-raised)] border p-4">
          <div className="text-sm text-charcoal-600">Staging</div>
          <div className="text-2xl font-semibold">{counts.byTier.staging}</div>
        </div>
        <div className="bg-[var(--bg-raised)] border p-4">
          <div className="text-sm text-charcoal-600">Dev</div>
          <div className="text-2xl font-semibold">{counts.byTier.dev}</div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-charcoal-600">Filter:</span>
        {(['all', 'prod', 'staging', 'dev'] as const).map(t => (
          <button key={t} className={`px-3 py-1.5 rounded border ${filter === t ? 'bg-consulting-slate-blue-50 text-consulting-slate-blue-700 border-consulting-slate-blue-200' : 'bg-[var(--bg-raised)] text-charcoal-700 border-charcoal-200'}`} onClick={() => setFilter(t as any)}>
            {String(t).toUpperCase()}
          </button>
        ))}
      </div>

      <div className="bg-[var(--bg-raised)] border">
        <div className="p-4 border-b font-semibold">Documents ({filtered.length})</div>
        <div className="divide-y">
          {filtered.map(d => (
            <div key={d.id} className="p-4 flex items-center justify-between">
              <div>
                <div className="font-medium">{d.title || d.path}</div>
                <div className="text-xs text-charcoal-600">{d.path} • {d.tier.toUpperCase()} • conf {d.confidence}</div>
              </div>
              <div className="flex items-center gap-2">
                {d.tier !== 'staging' && (
                  <button className="px-2 py-1.5 text-sm rounded border" onClick={() => copyPromote(d.path, 'staging')}>Copy Promote→Staging</button>
                )}
                {d.tier !== 'prod' && (
                  <button className="px-2 py-1.5 text-sm rounded border bg-consulting-slate-blue-600 text-white" onClick={() => copyPromote(d.path, 'prod')}>Copy Promote→Prod</button>
                )}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="p-4 text-sm text-charcoal-600">No documents</div>
          )}
        </div>
      </div>

      <div className="bg-[var(--bg-raised)] border">
        <div className="p-4 border-b font-semibold">Assessment</div>
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Explore inclusion of criteria in capability model</div>
              <div className="text-xs text-charcoal-600">Draft proposal for criteria attached to capabilities/families and impact on scoring/guidance.</div>
            </div>
            <button className="px-2 py-1.5 text-sm rounded border" onClick={() => navigator.clipboard.writeText('Task: Explore inclusion of criteria in capability model')}>Copy Task</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DocsDashboard

